import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Alert
} from '@mui/material';
import {
  Upload,
  PictureAsPdf,
  Delete,
  GetApp,
  Edit
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import api from '../lib/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import DocumentViewer from '../components/DocumentViewer';

interface DocumentManagementProps {
  embedded?: boolean;
}

const DocumentManagementPage: React.FC<DocumentManagementProps> = ({ embedded = false }) => {
  const { id: agreementId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState('AGREEMENT');
  const [fileName, setFileName] = useState('');
  const [editingDocument, setEditingDocument] = useState<any | null>(null);

  const { data: documents, isLoading } = useQuery({
    queryKey: ['documents', agreementId],
    queryFn: async () => {
      const res = await api.get(`/documents?agreementId=${agreementId}`);
      return res.data.data;
    },
    enabled: !!agreementId
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', documentType);
      formData.append('name', fileName || file.name);

      const res = await api.post(`/documents/upload?agreementId=${agreementId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', agreementId] });
      handleCloseDialog();
    }
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/documents/generate?agreementId=${agreementId}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', agreementId] });
    }
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setUploadFile(acceptedFiles[0]);
        if (!fileName) {
          setFileName(acceptedFiles[0].name);
        }
      }
    },
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'image/*': ['.jpg', '.jpeg', '.png']
    }
  });

  const handleOpenDialog = () => setOpenUploadDialog(true);
  const handleCloseDialog = () => {
    setOpenUploadDialog(false);
    setUploadFile(null);
    setFileName('');
  };

  const handleUpload = () => {
    if (!uploadFile) return;
    uploadMutation.mutate(uploadFile);
  };

  const handleGeneratePDF = () => {
    generateMutation.mutate();
  };

  const handleDownload = async (doc: { id: string; name: string }) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, {
        responseType: 'blob',
      });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: Blob } };
      if (axiosError.response?.data instanceof Blob) {
        const text = await axiosError.response.data.text();
        try {
          const parsed = JSON.parse(text);
          alert(parsed.error?.message || 'Download failed');
          return;
        } catch {
          /* not JSON */
        }
      }
      alert('Download failed. Ensure R2 is configured and the file was uploaded to storage.');
    }
  };

  const getDocumentTypeColor = (type: string) => {
    const colors: { [key: string]: any } = {
      'AGREEMENT': 'primary',
      'FORMATION': 'success',
      'BANKING': 'info',
      'TRAINING': 'warning',
      'COMPLIANCE': 'error',
      'OTHER': 'default'
    };
    return colors[type] || 'default';
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {!embedded && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h6">
            Documents
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdf />}
              onClick={handleGeneratePDF}
              disabled={generateMutation.isPending}
              size="small"
            >
              Generate
            </Button>
            <Button
              variant="contained"
              startIcon={<Upload />}
              onClick={handleOpenDialog}
              size="small"
            >
              Upload
            </Button>
          </Box>
        </Box>
      )}

      {embedded && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdf />}
              onClick={handleGeneratePDF}
              disabled={generateMutation.isPending}
              size="small"
            >
              Generate PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<Upload />}
              onClick={handleOpenDialog}
              size="small"
            >
              Upload
            </Button>
          </Box>
        </Box>
      )}

        {/* Documents Grid */}
        {documents && documents.length > 0 ? (
          <Grid container spacing={2}>
            {documents.map((doc: any) => (
              <Grid item xs={12} sm={6} md={4} key={doc.id}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PictureAsPdf color="error" />
                        <Typography variant="subtitle1" noWrap>
                          {doc.name}
                        </Typography>
                      </Box>
                      <Chip
                        label={doc.type}
                        color={getDocumentTypeColor(doc.type)}
                        size="small"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {doc.mimeType && `${doc.mimeType.split('/')[1].toUpperCase()}`}
                      {doc.fileSize && ` • ${(doc.fileSize / 1024).toFixed(1)} KB`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                      {format(new Date(doc.uploadedAt), 'MMM dd, yyyy')}
                    </Typography>
                    <Box display="flex" gap={1} mt={2}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Edit />}
                        onClick={() => setEditingDocument(doc)}
                      >
                        Edit Fields
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<GetApp />}
                        onClick={() => handleDownload(doc)}
                      >
                        Download
                      </Button>
                      <IconButton size="small" color="error">
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Alert severity="info">
            No documents uploaded yet. Upload a file or generate a PDF to get started.
          </Alert>
        )}

        {/* Document Editor Dialog */}
        <Dialog 
          open={!!editingDocument} 
          onClose={() => setEditingDocument(null)} 
          maxWidth="lg" 
          fullWidth
          PaperProps={{ sx: { height: '90vh' } }}
        >
          <DialogTitle>
            Edit Signature Fields - {editingDocument?.name}
          </DialogTitle>
          <DialogContent dividers>
            <DocumentViewer
              documentId={editingDocument?.id || ''}
              documentUrl={editingDocument?.url}
              mode="edit"
              onDownload={() => {
                // TODO: Implement download with fields
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingDocument(null)}>Close</Button>
            <Button variant="contained">Save Changes</Button>
          </DialogActions>
        </Dialog>

        {/* Upload Dialog */}
        <Dialog open={openUploadDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              {/* Dropzone */}
              <Box
                {...getRootProps()}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragActive ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: isDragActive ? 'action.hover' : 'transparent',
                  transition: 'all 0.2s'
                }}
              >
                <input {...getInputProps()} />
                <Upload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                {uploadFile ? (
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {uploadFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(uploadFile.size / 1024).toFixed(1)} KB
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body1">
                      {isDragActive ? 'Drop file here' : 'Drag & drop file or click to browse'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      PDF, DOC, DOCX, JPG, PNG (max 10MB)
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* File Name */}
              <TextField
                fullWidth
                label="Document Name"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="Enter document name"
              />

              {/* Document Type */}
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  label="Type"
                >
                  <MenuItem value="AGREEMENT">Agreement</MenuItem>
                  <MenuItem value="FORMATION">Formation</MenuItem>
                  <MenuItem value="BANKING">Banking</MenuItem>
                  <MenuItem value="TRAINING">Training</MenuItem>
                  <MenuItem value="COMPLIANCE">Compliance</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!uploadFile || uploadMutation.isPending}
            >
              {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
  );
};

export default DocumentManagementPage;

